import { useEffect, useState, useMemo } from 'react';
import api from '../utils/api';
import { Plus, Pencil, Trash2, AlertTriangle, Package, Printer, Search, CircleDot, Box, Sparkles, Copy } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

// ── 생산월 기준 경과 계산 ──
function getAgeBadge(productionMonth) {
  if (!productionMonth) return null;
  const months = dayjs().diff(dayjs(productionMonth + '-01'), 'month');
  if (months >= 12) return { label: '1년초과', color: 'text-red-400 bg-red-400/15' };
  if (months >= 6) return { label: '6개월초과', color: 'text-orange-400 bg-orange-400/15' };
  return null;
}

function getCardBg(productionMonth) {
  if (!productionMonth) return 'bg-gray-900';
  const months = dayjs().diff(dayjs(productionMonth + '-01'), 'month');
  if (months >= 12) return 'bg-red-950/40';
  if (months >= 6) return 'bg-orange-950/30';
  return 'bg-gray-900';
}

// ── 재고 등록/수정 모달 ──
function StockModal({ stock, onSave, onClose }) {
  // 수정 시: 잔량 === 용량이면 새거, 아니면 사용한거
  const initType = (stock?.remainingWeightGram != null && stock?.totalWeightGram != null)
    ? (stock.remainingWeightGram >= stock.totalWeightGram ? 'new' : 'used')
    : 'new';

  const [type, setType] = useState(initType);
  const [form, setForm] = useState(() => {
    const base = stock || {
      brand: '', material: '', productName: '', colorName: '', colorHex: '#ffffff',
      totalWeightGram: 1000, remainingWeightGram: 1000, spoolCount: 1,
      reorderThresholdGram: 2000, productionMonth: '', notes: '',
    };
    return { ...base };
  });

  const isNew = type === 'new';

  // 새거: 잔량 = 용량 × 스풀 수 자동 동기화
  const handleTotalChange = (val) => {
    const v = +val;
    if (isNew) {
      setForm(f => ({ ...f, totalWeightGram: v, remainingWeightGram: v * f.spoolCount }));
    } else {
      setForm(f => ({ ...f, totalWeightGram: v, remainingWeightGram: Math.min(f.remainingWeightGram, v) }));
    }
  };

  const handleSpoolChange = (val) => {
    const v = Math.max(1, +val);
    if (isNew) {
      setForm(f => ({ ...f, spoolCount: v, remainingWeightGram: f.totalWeightGram * v }));
    } else {
      setForm(f => ({ ...f, spoolCount: v }));
    }
  };

  // 사용한거: 잔량 <= 용량 제한
  const handleRemainingChange = (val) => {
    const v = Math.min(+val, form.totalWeightGram);
    setForm(f => ({ ...f, remainingWeightGram: v }));
  };

  // 재주문 기준: 용량 × 스풀 수 초과 불가
  const maxReorder = form.totalWeightGram * (form.spoolCount || 1);
  const handleReorderChange = (val) => {
    const v = Math.min(+val, maxReorder);
    setForm(f => ({ ...f, reorderThresholdGram: v }));
  };

  // 타입 전환
  const handleTypeChange = (t) => {
    setType(t);
    if (t === 'new') {
      setForm(f => ({ ...f, remainingWeightGram: f.totalWeightGram * Math.max(f.spoolCount, 1), spoolCount: Math.max(f.spoolCount, 1) }));
    } else {
      setForm(f => ({ ...f, spoolCount: 1, reorderThresholdGram: 0 }));
    }
  };

  const handleSave = async () => {
    const payload = { ...form };
    if (!isNew) payload.reorderThresholdGram = 0;
    if (stock?.id) {
      await api.put(`/filament/stock/${stock.id}`, payload);
    } else {
      await api.post('/filament/stock', payload);
    }
    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md">
        <h2 className="text-white font-bold mb-4">{stock?.id ? '재고 수정' : '재고 등록'}</h2>
        <div className="space-y-3">
          {/* 새거 / 사용한거 */}
          <div>
            <label className="text-gray-400 text-xs mb-1 block">구분</label>
            <div className="flex gap-2">
              {[{ value: 'new', label: '새거' }, { value: 'used', label: '사용한거' }].map(opt => (
                <button key={opt.value} type="button" onClick={() => handleTypeChange(opt.value)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    type === opt.value ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">재질</label>
              <select value={form.material || ''} onChange={e => setForm(f => ({ ...f, material: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand">
                <option value="">선택</option>
                <option value="PLA">PLA</option>
                <option value="TPU">TPU</option>
                <option value="Foam TPU">Foam TPU</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">브랜드</label>
              <input value={form.brand || ''} placeholder="Bambu Lab"
                onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">품명</label>
            <input value={form.productName || ''} placeholder="PLA Basic / TPU 95A HF 등"
              onChange={e => setForm(f => ({ ...f, productName: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">색상</label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.colorHex || '#ffffff'}
                onChange={e => setForm(f => ({ ...f, colorHex: e.target.value }))}
                className="w-10 h-9 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer flex-shrink-0" />
              <input value={form.colorName || ''} placeholder="Ivory White"
                onChange={e => setForm(f => ({ ...f, colorName: e.target.value }))}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">생산월</label>
              <input type="month" value={form.productionMonth || ''}
                onChange={e => setForm(f => ({ ...f, productionMonth: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">스풀당 용량(g)</label>
              <input type="number" value={form.totalWeightGram || 1000}
                onChange={e => handleTotalChange(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {isNew ? (
              <div>
                <label className="text-gray-400 text-xs mb-1 block">보유 스풀 수</label>
                <input type="number" value={form.spoolCount || 1} min={1}
                  onChange={e => handleSpoolChange(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
              </div>
            ) : (
              <div>
                <label className="text-gray-400 text-xs mb-1 block">잔량(g)</label>
                <input type="number" value={form.remainingWeightGram || 0} max={form.totalWeightGram}
                  onChange={e => handleRemainingChange(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
              </div>
            )}
            {isNew ? (
              <div>
                <label className="text-gray-400 text-xs mb-1 block">잔량(g)</label>
                <input type="number" value={form.remainingWeightGram} disabled
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-gray-500 text-sm cursor-not-allowed" />
              </div>
            ) : (
              <div>
                <label className="text-gray-400 text-xs mb-1 block">스풀 수</label>
                <input type="number" value={1} disabled
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-gray-500 text-sm cursor-not-allowed" />
              </div>
            )}
          </div>
          {isNew && (
            <div>
              <label className="text-gray-400 text-xs mb-1 block">재주문 기준 (총 잔량이 이 이하면 경고, 최대 {maxReorder}g)</label>
              <input type="number" value={form.reorderThresholdGram} max={maxReorder}
                onChange={e => handleReorderChange(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
            </div>
          )}
          <div>
            <label className="text-gray-400 text-xs mb-1 block">메모</label>
            <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="특이사항, 주문처, 구매링크 등"
              rows={2}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand resize-none" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg py-2 text-sm">취소</button>
          <button onClick={handleSave} className="flex-1 bg-brand hover:bg-brand-600 text-white rounded-lg py-2 text-sm font-medium">저장</button>
        </div>
      </div>
    </div>
  );
}

// ── 카드 컴포넌트 ──
function StockCard({ s, onEdit, onDelete, onCopy, mountedPrinter }) {
  const pct = Math.min(100, Math.round((s.remainingWeightGram / (s.totalWeightGram * (s.spoolCount || 1))) * 100));
  const total = s.remainingWeightGram * s.spoolCount;
  const needReorder = total < s.reorderThresholdGram;
  const ageBadge = getAgeBadge(s.productionMonth);
  const cardBg = getCardBg(s.productionMonth);

  return (
    <div className={`${cardBg} border rounded-xl p-4 ${needReorder ? 'border-yellow-500/40' : 'border-gray-800'}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="w-5 h-5 rounded-full flex-shrink-0 border border-gray-600"
            style={{ backgroundColor: s.colorHex || '#888' }} />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                s.material === 'PLA' ? 'bg-blue-500/20 text-blue-400'
                : s.material === 'TPU' ? 'bg-emerald-500/20 text-emerald-400'
                : s.material === 'Foam TPU' ? 'bg-purple-500/20 text-purple-400'
                : 'bg-gray-700 text-gray-400'
              }`}>{s.material || '?'}</span>
              <p className="text-white font-bold">{s.productName || s.colorName || '품명 미입력'}</p>
              {ageBadge && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ageBadge.color}`}>{ageBadge.label}</span>
              )}
            </div>
            <p className="text-gray-500 text-xs">{s.colorName} · {s.brand}</p>
          </div>
        </div>
        <div className="flex gap-0.5">
          <button onClick={onCopy} title="복사 등록" className="p-1.5 text-gray-600 hover:text-blue-400 rounded hover:bg-gray-800">
            <Copy size={12} />
          </button>
          <button onClick={onEdit} className="p-1.5 text-gray-600 hover:text-white rounded hover:bg-gray-800">
            <Pencil size={12} />
          </button>
          <button onClick={onDelete} className="p-1.5 text-gray-600 hover:text-red-400 rounded hover:bg-gray-800">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      {mountedPrinter && (
        <div className="flex items-center gap-1.5 mb-2 bg-brand/10 rounded-lg px-2.5 py-1.5">
          <Printer size={12} className="text-brand flex-shrink-0" />
          <span className="text-brand text-xs font-medium">{mountedPrinter}</span>
        </div>
      )}
      <div className="w-full bg-gray-700 rounded-full h-1.5 mb-2">
        <div className={`h-1.5 rounded-full ${needReorder ? 'bg-yellow-400' : 'bg-brand'}`}
          style={{ width: `${Math.max(3, pct)}%` }} />
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-400">{s.totalWeightGram}g × {s.spoolCount}스풀</span>
        {s.productionMonth && (
          <span className="text-gray-600">생산 {s.productionMonth}</span>
        )}
      </div>
    </div>
  );
}

// ── 섹션 헤더 ──
function SectionHeader({ icon: Icon, title, count, color }) {
  return (
    <div className="flex items-center gap-2 mb-3 mt-6 first:mt-0">
      <Icon size={16} className={color} />
      <h2 className="text-white font-semibold text-sm">{title}</h2>
      <span className="text-gray-600 text-xs">({count})</span>
    </div>
  );
}

// ── 메인 ──
export default function FilamentPage() {
  const [stocks, setStocks] = useState([]);
  const [slots, setSlots] = useState([]);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');

  const load = () => {
    api.get('/filament/stock').then(r => setStocks(r.data));
    api.get('/filament/slots').then(r => setSlots(r.data));
  };
  useEffect(() => { load(); }, []);

  const del = async id => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/filament/stock/${id}`);
    load();
  };

  // id 제거 후 모달 열어 복사 등록
  const copy = (s) => {
    const { id, createdAt, updatedAt, ...rest } = s;
    setModal({ ...rest });
  };

  // 검색 필터 (재고만)
  const filtered = useMemo(() => {
    if (!search.trim()) return stocks;
    const q = search.toLowerCase();
    return stocks.filter(s =>
      (s.material || '').toLowerCase().includes(q) ||
      (s.brand || '').toLowerCase().includes(q) ||
      (s.productName || '').toLowerCase().includes(q) ||
      (s.colorName || '').toLowerCase().includes(q)
    );
  }, [stocks, search]);

  // 슬롯 검색 필터
  const filteredSlots = useMemo(() => {
    if (!search.trim()) return slots;
    const q = search.toLowerCase();
    return slots.filter(s =>
      (s.material || '').toLowerCase().includes(q) ||
      (s.brand || '').toLowerCase().includes(q) ||
      (s.colorName || '').toLowerCase().includes(q)
    );
  }, [slots, search]);

  // 재고는 새/잔여로만 분류 (장착 여부와 무관)
  const { remaining, fresh } = useMemo(() => {
    const r = [], f = [];
    for (const s of filtered) {
      const pct = Math.min(100, Math.round((s.remainingWeightGram / (s.totalWeightGram * (s.spoolCount || 1))) * 100));
      if (pct >= 100) f.push(s);
      else r.push(s);
    }
    return { remaining: r, fresh: f };
  }, [filtered]);

  const totalSpools = stocks.reduce((s, x) => s + x.spoolCount, 0);

  return (
    <div>
      <PageHeader icon={Package} title="필라멘트 관리" count={stocks.length}>
        <button onClick={() => setModal({})}
          className="flex items-center gap-2 bg-brand hover:bg-brand-600 text-white rounded-lg px-4 py-2 text-sm font-medium">
          <Plus size={16} /> 재고 등록
        </button>
      </PageHeader>

      {/* 검색 */}
      <div className="relative mb-5">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="재질, 브랜드, 품명, 색상명으로 검색..."
          className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600" />
      </div>

      {filtered.length === 0 && filteredSlots.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-16 text-center">
          <Package size={40} className="text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">{search ? '검색 결과가 없습니다.' : '등록된 재고가 없습니다.'}</p>
          {!search && (
            <button onClick={() => setModal({})} className="mt-3 text-brand text-sm hover:underline">
              첫 번째 재고 등록하기
            </button>
          )}
        </div>
      ) : (
        <>
          {/* 장착 필라멘트 — 슬롯 기반 */}
          {filteredSlots.length > 0 && (
            <>
              <SectionHeader icon={Printer} title="장착 필라멘트" count={filteredSlots.length} color="text-brand" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {filteredSlots.map(sl => {
                  const slotAsStock = {
                    id: `slot-${sl.id}`,
                    material: sl.material,
                    productName: sl.stock?.productName || '',
                    colorName: sl.colorName,
                    colorHex: sl.colorHex,
                    brand: sl.brand,
                    totalWeightGram: sl.totalWeightGram,
                    remainingWeightGram: sl.remainingWeightGram,
                    spoolCount: 1,
                    productionMonth: sl.stock?.productionMonth,
                    reorderThresholdGram: 0,
                  };
                  const printerName = sl.printer?.name || `프린터#${sl.printerId}`;
                  const side = sl.slotNo === 0 ? 'L' : 'R';
                  return (
                    <StockCard key={slotAsStock.id} s={slotAsStock}
                      onEdit={() => {}} onDelete={() => {}}
                      mountedPrinter={`${printerName} (${side})`} />
                  );
                })}
              </div>
            </>
          )}

          {/* 잔여 필라멘트 */}
          {remaining.length > 0 && (
            <>
              <SectionHeader icon={CircleDot} title="잔여 필라멘트" count={remaining.length} color="text-yellow-400" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {remaining.map(s => (
                  <StockCard key={s.id} s={s} onEdit={() => setModal(s)} onDelete={() => del(s.id)} onCopy={() => copy(s)} />
                ))}
              </div>
            </>
          )}

          {/* 새 필라멘트 */}
          {fresh.length > 0 && (
            <>
              <SectionHeader icon={Sparkles} title="새 필라멘트" count={fresh.length} color="text-green-400" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {fresh.map(s => (
                  <StockCard key={s.id} s={s} onEdit={() => setModal(s)} onDelete={() => del(s.id)} onCopy={() => copy(s)} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {modal !== null && (
        <StockModal
          stock={modal?.id ? modal : (modal?.brand ? modal : null)}
          onSave={() => { load(); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
